/**
 * Audit app — Belenios verifier, browser-only, AGPLv3.
 *
 * Two file kinds, auto-detected:
 *   - bulletin.json (transcript of a closed ballot) → verifyTranscript
 *   - audit-…json   (Benaloh challenge from a voter) → verifyBenalohChallenge
 *
 * No network calls after page load; the page can be saved (Ctrl-S) and
 * re-run offline.
 */
import {
  bytesToHex,
  encodePoint,
  parseBenalohChallenge,
  parseTranscript,
  verifyBenalohChallenge,
  verifyTranscript,
  verifyTranscriptDpoSignature,
  type BenalohChallenge,
  type DpoSignatureStatus,
  type RistrettoPoint,
  type Transcript,
} from "@oar-digital/voting";

const fileInput = document.querySelector<HTMLInputElement>("#file-input")!;
const dropzone = document.querySelector<HTMLElement>(".dropzone-card")!;
const resultRoot = document.querySelector<HTMLElement>("#result")!;

fileInput.addEventListener("change", (e) => {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (file) handleFile(file);
});

dropzone.addEventListener("dragover", (e) => {
  e.preventDefault();
  dropzone.classList.add("dragover");
});
dropzone.addEventListener("dragleave", () => {
  dropzone.classList.remove("dragover");
});
dropzone.addEventListener("drop", (e) => {
  e.preventDefault();
  dropzone.classList.remove("dragover");
  const file = e.dataTransfer?.files[0];
  if (file) handleFile(file);
});

async function handleFile(file: File): Promise<void> {
  resultRoot.innerHTML =
    '<p style="color: var(--ink-700); font-style: italic;">Se verifică…</p>';

  let raw: string;
  try {
    raw = await file.text();
  } catch (err) {
    return showError("Nu pot citi fișierul.", err);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    return showError("JSON invalid.", err);
  }

  // Dispatch by `kind` field — Benaloh challenges carry it; transcripts don't.
  if (looksLikeChallenge(parsed)) {
    handleChallenge(parsed);
    return;
  }
  handleTranscript(parsed);
}

function looksLikeChallenge(v: unknown): boolean {
  return (
    typeof v === "object" &&
    v !== null &&
    !Array.isArray(v) &&
    (v as Record<string, unknown>)["kind"] === "BenalohChallenge"
  );
}

// ---------------------------------------------------------------------------
// Transcript path
// ---------------------------------------------------------------------------

function handleTranscript(parsed: unknown): void {
  let transcript;
  try {
    transcript = parseTranscript(parsed);
  } catch (err) {
    return showError("Forma transcriptului nu se potrivește.", err);
  }

  const startedAt = performance.now();
  const result = verifyTranscript(transcript);
  const elapsed = Math.round(performance.now() - startedAt);
  const dpoStatus = verifyTranscriptDpoSignature(transcript);

  if (result.valid) {
    showTranscriptOk(transcript, elapsed, dpoStatus);
  } else {
    showTranscriptInvalid(transcript, result.errors, elapsed, dpoStatus);
  }
}

function showTranscriptOk(
  t: Transcript,
  elapsed: number,
  dpoStatus: DpoSignatureStatus,
): void {
  const counts = t.options.map((opt, i) => ({
    label: opt.label,
    count: t.tally.counts[i] ?? 0,
  }));
  const max = counts.reduce((a, c) => Math.max(a, c.count), 1);
  const total = counts.reduce((a, c) => a + Math.max(c.count, 0), 0);

  resultRoot.innerHTML = `
    <div class="result-ok">
      <h2 style="margin-top: 0;">✓ Rezultat confirmat</h2>
      <p>Am verificat fișierul în browserul tău (${elapsed}ms). <strong>Numărătoarea publicată e cea reală</strong> — fiecare vot a fost numărat corect, nimeni nu a schimbat nimic pe drum.</p>
      ${renderDpoStatus(dpoStatus)}
      <dl class="result-meta">
        <dt>Vot</dt>
        <dd>${escapeHtml(t.title)}</dd>
        <dt>Voturi numărate</dt>
        <dd>${t.bulletin.length}</dd>
        <dt>Decriptat de</dt>
        <dd>${t.tally.trusteeSubset.length} din ${t.dkg.trustees.length} membri ai Comisiei Electorale (prag minim ${t.dkg.threshold})</dd>
      </dl>
      <h3 style="margin-top: 1.5rem; margin-bottom: 0.5rem;">Rezultat</h3>
      <p style="margin: 0 0 0.5rem 0; font-size: 0.85rem;">Total buletine numărate: <strong>${total}</strong></p>
      ${counts
        .map(
          (c) => `
            <div class="tally-bar">
              <span class="label" title="${escapeHtml(c.label)}">${escapeHtml(c.label)}</span>
              <div class="bar-track">
                <div class="bar" style="width: ${Math.max(2, (c.count / max) * 100)}%;"></div>
              </div>
              <span class="count">${c.count}</span>
            </div>`,
        )
        .join("")}
    </div>
  `;
}

function renderDpoStatus(status: DpoSignatureStatus): string {
  if (status.kind === "valid") {
    return `
      <div style="margin: 0.75rem 0; padding: 0.75rem; border: 1px solid #166534; background: #f0fdf4; font-size: 0.85rem;">
        <strong>✓ Procesul verbal e semnat oficial.</strong>
        Semnat de ${escapeHtml(status.signedBy)} la ${escapeHtml(status.signedAt)}.
        Pentru o verificare suplimentară, compară amprenta semnăturii (de mai jos) cu cea publicată oficial la
        <a href="https://app.oar.digital/despre/cripto" target="_blank" rel="noopener">/despre/cripto</a>:
        <br><code style="font-size: 0.7rem; word-break: break-all; display: block; margin-top: 0.25rem;">${escapeHtml(status.publicKeyHex)}</code>
      </div>
    `;
  }
  if (status.kind === "absent") {
    return `
      <div style="margin: 0.75rem 0; padding: 0.75rem; border: 1px solid #ca8a04; background: #fefce8; font-size: 0.85rem;">
        <strong>⚠ Procesul verbal nu poartă semnătura oficială.</strong>
        Numărătoarea în sine e validă (am verificat-o mai sus), dar lipsește
        atestarea că aceasta e versiunea publicată oficial. Pentru pilotul
        consultativ e acceptabil; pentru un scrutin cu efect juridic, nu.
      </div>
    `;
  }
  return `
    <div style="margin: 0.75rem 0; padding: 0.75rem; border: 1px solid #b91c1c; background: #fef2f2; font-size: 0.85rem;">
      <strong>✗ Semnătura oficială este INVALIDĂ.</strong>
      Cineva a modificat fișierul după ce a fost semnat. Anunță Comisia Electorală.
      <br><span style="font-size: 0.7rem; color: #7f1d1d;">Detaliu tehnic: ${escapeHtml(status.reason)}</span>
    </div>
  `;
}

function showTranscriptInvalid(
  t: Transcript,
  errors: ReadonlyArray<string>,
  elapsed: number,
  dpoStatus: DpoSignatureStatus,
): void {
  resultRoot.innerHTML = `
    <div class="result-fail">
      <h2 style="margin-top: 0;">✗ Numărătoare incorectă</h2>
      <p>Fișierul nu trece verificarea (${elapsed}ms). Asta înseamnă că numărătoarea publicată <strong>nu corespunde</strong> cu voturile efectiv puse — fie cineva a modificat fișierul, fie sistemul de vot are o eroare. Anunță Comisia Electorală.</p>
      <p style="font-size: 0.85rem; color: var(--ink-700);"><strong>Detalii tehnice:</strong></p>
      <ul class="result-error-list">
        ${errors.map((e) => `<li>${escapeHtml(e)}</li>`).join("")}
      </ul>
      ${renderDpoStatus(dpoStatus)}
      <dl class="result-meta">
        <dt>Buletin</dt>
        <dd>${escapeHtml(t.ballotId)} — ${escapeHtml(t.title)}</dd>
      </dl>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Benaloh challenge path
// ---------------------------------------------------------------------------

function handleChallenge(parsed: unknown): void {
  let result;
  try {
    result = parseBenalohChallenge(parsed);
  } catch (err) {
    return showError("Forma fișierului de audit nu se potrivește.", err);
  }
  const { challenge, advertisedBallotPublicKey } = result;

  const startedAt = performance.now();
  const verify = verifyBenalohChallenge({
    challenge,
    ballotPublicKey: advertisedBallotPublicKey,
  });
  const elapsed = Math.round(performance.now() - startedAt);

  if (verify.valid) {
    showChallengeOk(challenge, advertisedBallotPublicKey, elapsed);
  } else {
    showChallengeInvalid(challenge, verify.errors, elapsed);
  }
}

function showChallengeOk(
  c: BenalohChallenge,
  pk: RistrettoPoint,
  elapsed: number,
): void {
  const chosenLabel = c.chosen.join(", ");
  const chosenWord = c.chosen.length === 1 ? "opțiunea" : `cele ${c.chosen.length} opțiuni`;
  resultRoot.innerHTML = `
    <div class="result-ok">
      <h2 style="margin-top: 0;">✓ Browserul tău a votat exact ce ai cerut</h2>
      <p>Am re-făcut local (${elapsed}ms) calculul pe care l-a făcut browserul tău când ai apăsat „Auditează”, și a ieșit identic. Concluzie: ${chosenWord} pe care browserul a criptat-o este <strong>${escapeHtml(chosenLabel)}</strong>. Adică exact ce ai văzut pe ecran și ce ai vrut să votezi.</p>
      <p style="margin-top: 1rem; padding: 0.75rem; border: 1px solid var(--ink-200); background: var(--ink-50); font-size: 0.85rem;">
        Acest buletin <strong>nu a fost trimis</strong> ca vot — auditarea
        revelă opțiunea ta, deci ar strica secretul. Pentru votul real,
        criptezi din nou și apeși „Trimite vot”.
      </p>
      <dl class="result-meta">
        <dt>Vot</dt>
        <dd>${escapeHtml(c.ballotId)}</dd>
        <dt>Opțiuni disponibile</dt>
        <dd>${c.options.map((o) => escapeHtml(o)).join(", ")}</dd>
        <dt>Locuri</dt>
        <dd>${c.seats}</dd>
        <dt>${c.chosen.length === 1 ? "Opțiunea aleasă" : "Opțiunile alese"}</dt>
        <dd><strong>${escapeHtml(chosenLabel)}</strong></dd>
      </dl>
      <details style="margin-top: 1rem; font-size: 0.8rem; color: var(--ink-700);">
        <summary>Detalii tehnice</summary>
        <dl class="result-meta">
          <dt>Cheia electorală (din fișier)</dt>
          <dd><code style="font-size: 0.7rem; word-break: break-all;">${escapeHtml(bytesToHex(encodePoint(pk)))}</code></dd>
          <dt>Cheia votantului (din plic)</dt>
          <dd><code style="font-size: 0.7rem; word-break: break-all;">${escapeHtml(bytesToHex(encodePoint(c.credentialPublicKey)))}</code></dd>
        </dl>
        <p>Compară cheia electorală cu cea publicată pe tabla oficială
        (<code>/alegeri/&lt;id&gt;/bulletin</code>). Dacă diferă, browserul
        a folosit o cheie greșită — anunță Comisia Electorală.</p>
      </details>
    </div>
  `;
}

function showChallengeInvalid(
  c: BenalohChallenge,
  errors: ReadonlyArray<string>,
  elapsed: number,
): void {
  resultRoot.innerHTML = `
    <div class="result-fail">
      <h2 style="margin-top: 0;">✗ Browserul tău NU a votat ce ai cerut</h2>
      <p>Am re-făcut calculul (${elapsed}ms) și nu se potrivește. Asta înseamnă că <strong>browserul a criptat altceva decât ai văzut pe ecran</strong>, sau fișierul de audit a fost modificat după ce l-ai descărcat.</p>
      <dl class="result-meta">
        <dt>Vot</dt>
        <dd>${escapeHtml(c.ballotId)}</dd>
        <dt>${c.chosen.length === 1 ? "Opțiunea declarată" : "Opțiunile declarate"} pe ecran</dt>
        <dd>${escapeHtml(c.chosen.join(", "))}</dd>
      </dl>
      <p style="margin-top: 1rem; padding: 0.75rem; border: 1px solid var(--brand-500); background: rgba(188,29,35,0.05); font-size: 0.85rem;">
        <strong>Anunță imediat Comisia Electorală</strong> și NU folosi
        browserul/dispozitivul respectiv pentru a vota. Păstrează fișierul
        de audit ca dovadă.
      </p>
      <details style="margin-top: 1rem; font-size: 0.8rem; color: var(--ink-700);">
        <summary>Detalii tehnice</summary>
        <ul class="result-error-list">
          ${errors.map((e) => `<li>${escapeHtml(e)}</li>`).join("")}
        </ul>
      </details>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

function showError(headline: string, err: unknown): void {
  const detail = err instanceof Error ? err.message : String(err);
  resultRoot.innerHTML = `
    <div class="result-fail">
      <h2 style="margin-top: 0;">Eroare</h2>
      <p><strong>${escapeHtml(headline)}</strong></p>
      <p style="font-family: ui-monospace, monospace; font-size: 0.78rem; margin-top: 0.5rem;">${escapeHtml(detail)}</p>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
